import Group from 'components/utils/Group'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray, groupBy, min, sortByAll, sum } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import Pane from 'components/Common/Pane'
import MobileMultipleRoomsSameRoomOption from './MobileMultipleRoomsSameRoomOption'
import { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getDurationPackageOptions } from 'selectors/offerPage/offerPageSelectors'

interface Props {
  rooms: Array<OfferPageRoom>
  headerRef: React.RefObject<HTMLDivElement | null>
  showSelection: boolean
  durationPackageOptions: Array<App.OfferPackageOptionView>
}

function sortByAvailability(options: Array<App.OfferPackageOptionView>, roomsLength: number) {
  return options[0].availableRooms ?? 0 - roomsLength
}

function sortByPrice(options: Array<App.OfferPackageOptionView>) {
  return min(options, option => option.hotelTotal)
}

function sortBySelectedAvailability(options: Array<App.OfferPackageOptionView>, showSelection: boolean, rooms: Array<OfferPageRoom>) {
  if (!showSelection) return 0
  const availableRoomsTotal = sum(options, option => option.availableRooms ?? 0)
  if (availableRoomsTotal < rooms.length) return 0
  // Same for both rooms
  const optionRoomType = options[0].package.roomType?.id
  const isSelected = rooms.some(selectedRoom => selectedRoom.roomId === optionRoomType)
  return isSelected ? 1 : 0
}

function MobileMultipleRoomsSameRoomContent(props: Props) {
  const { rooms, durationPackageOptions, showSelection, headerRef } = props
  const optionsByRoomType = useMemo(() => {
    const rawOptions = Array.from(groupBy(durationPackageOptions, (pkg) => pkg?.package.roomType?.id).values())
    return sortByAll(rawOptions, [
      // First order by availability of rooms in the room type from having the most to the least
      { selector: pkg => sortByAvailability(pkg, rooms.length), direction: 'desc' },
      // Then order by price
      { selector: sortByPrice, direction: 'asc' },
      // First order by room that has a package selection (if selection is to be shown)
      { selector: pkg => sortBySelectedAvailability(pkg, showSelection, rooms), direction: 'desc' },
    ])
  },
  [durationPackageOptions, rooms, showSelection])

  return (
    <Pane background="neutral-six">
      <Group direction="vertical" gap={8}>
        {optionsByRoomType.map((roomOptions) => <MobileMultipleRoomsSameRoomOption
          key={roomOptions[0].package.roomType?.id}
          packageOptions={roomOptions}
          showSelection={showSelection}
          headerRef={headerRef}
        />)}
      </Group>
    </Pane>
  )
}

type MappedProps = Pick<Props,
  | 'rooms'
  | 'durationPackageOptions'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  rooms: state.rooms ?? EmptyArray,
  durationPackageOptions: getDurationPackageOptions(state, appState),
}), MobileMultipleRoomsSameRoomContent)
